/* eslint-disable react/prop-types */
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
// @mui
import {
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
// components
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { fetchManualLogs } from '../../services/timelineService';
import Iconify from '../iconify';
import Scrollbar from '../scrollbar';

export default function ChangeLogPopover() {
  const [manualLogs, setManualLogs] = useState([]);
  const [open, setOpen] = useState(null);
  const userName = useSelector((state) => state?.account?.data?.userName || null);

  const handleFetchChangeLogs = async () => {
    try {
      const response = await fetchManualLogs();
      setManualLogs(response?.data?.data);
    } catch (error) {
      toast.error(error?.response?.data?.message);
    }
  };

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  useEffect(() => {
    // dispatch(getProfile());
    handleFetchChangeLogs();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Button startIcon={<Iconify icon={'ic:round-history'} />} onClick={handleOpen}>
        Change log
      </Button>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            my: 1.5,
            ml: 0.75,
            width: { xs: '80vw', md: '50vw' },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" sx={{ fontSize: { xs: '.7rem', sm: '1rem' } }}>
              History of manual changes
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', display: { xs: 'none', md: 'block' } }}>
              This is the history of important changes in the {userName}'s timeline
            </Typography>
          </Box>

          <Tooltip title=" Close">
            <IconButton color="primary" onClick={handleClose}>
              <Iconify icon="ic:round-clear" />
            </IconButton>
          </Tooltip>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Scrollbar sx={{ height: { xs: 340, sm: 'auto' } }}>
          <List disablePadding>
            {manualLogs.map((changelog) => (
              <LogItem key={changelog.id} changelog={changelog} />
            ))}
          </List>
        </Scrollbar>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Box sx={{ p: 1 }}>
          <Button fullWidth disableRipple onClick={handleClose}>
            Cancel
          </Button>
        </Box>
      </Popover>
    </>
  );
}

LogItem.propTypes = {
  changelog: PropTypes.shape({
    createdAt: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isUnRead: PropTypes.bool,
    title: PropTypes.string,
    description: PropTypes.string,
    type: PropTypes.string,
    avatar: PropTypes.any,
  }),
};

function LogItem({ changelog }) {
  const startTime = dayjs(changelog?.startTime).format('h:mm:ss A');
  const stopTime = dayjs(changelog?.stopTime).format('h:mm:ss A');
  const workedDate = dayjs(changelog?.startTime).format('DD/MM/YYYY');
  const modifiedDate = dayjs(changelog?.updatedAt).format('DD/MM/YYYY, h:mm:ss A');
  const isCreated = changelog?.createdAt === changelog?.updatedAt;
  const modifiedBy = changelog?.users?.userName;
  const durationMilli = dayjs(changelog?.stopTime).diff(dayjs(changelog?.startTime));
  const durationHours = Math.floor(durationMilli / (1000 * 60 * 60));
  const durationMinute = Math.floor((durationMilli % (1000 * 60 * 60)) / (1000 * 60));

  return (
    <ListItemButton
      sx={{
        py: 1.5,
        px: 2.5,
        mt: '1px',
      }}
    >
      <ListItemText
        primary={
          <>
            <Typography variant="subtitle2" sx={{ color: '#5da739' }}>
              {modifiedDate}
            </Typography>
            <Typography variant="subtitle2">
              {isCreated ? 'Time entry manually added' : 'Time entry manually updated'}
              <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
                &nbsp; ({modifiedBy})
              </Typography>
            </Typography>
          </>
        }
        secondary={
          <>
            <Typography
              variant="caption"
              sx={{
                mt: 0.5,
                display: 'flex',
                alignItems: 'center',
                color: 'text.disabled',
              }}
            >
              {startTime} - {stopTime} ({workedDate})
            </Typography>
            <Typography
              variant="caption"
              sx={{
                mt: 0.5,
                display: 'flex',
                alignItems: 'center',
                color: 'text.disabled',
              }}
            >
              Duration: {durationHours}h {durationMinute}m
            </Typography>
            <Typography
              variant="caption"
              sx={{
                mt: 0.5,
                display: 'flex',
                alignItems: 'center',
                color: 'text.disabled',
              }}
            >
              Description: {changelog?.tasks?.taskName}
            </Typography>
          </>
        }
      />
    </ListItemButton>
  );
}
